// 🔒 SERVIÇO DE ANONIMIZAÇÃO DE DADOS
// Sistema: HEPATO TX RONDÔNIA
// Arquivo: src/modules/ia-analise/services/anonimizarDados.ts
// VERSÃO 2.0 - Integração com achados de imagem estruturados

import { Paciente } from "../../../types";
// B351 passo 5b (AI_153) — dados exclusivos da modalidade doador de fígado.
import { ehDoadorFigado } from "../../../shared/utils/modalidadeAtendimento";
import { statusInscricaoParaIA } from "../../../shared/utils/statusInscricaoIA";
import { ConjuntoMinimoReceptor } from "../../../shared/utils/conjuntoMinimoReceptor";
import { montarAvaliacaoDoadorParaIA } from "./avaliacaoDoadorParaIA";
import {
  DadosAnonimizados,
  AchadosImagemEstruturados,
  MarcadorAnonimizado,
} from "../types";
// B247 (AI_111): a IA passa a ler sorologias/autoanticorpos PADRONIZADOS. Versão
// "manterOriginal" = se não reconhecer o resultado, preserva o valor cru (a IA
// nunca perde dado). Não altera prompt nem regra clínica — só limpa a leitura.
import {
  padronizarRegistroSorologia,
  padronizarRegistroMarcadores,
} from "../../sorologias/services/normalizarSorologia";
import { meldNaDoExameMaisRecente } from "../../../shared/utils/meldNa";
import {
  registrarRGCT,
  marcadorRGCT,
  substituirRGCTEmTexto,
  padroesNomePaciente,
  padroesNomeMae,
  redigirNomeEmObjeto,
} from "../../../shared/utils/reidentificacao";

/**
 * 🗓️ NORMALIZA DATA PARA STRING ISO YYYY-MM-DD
 *
 * Aceita os 5 formatos de data que coexistem no Firestore do hepato.app
 * (consequência de migrações de tipo ao longo da evolução do sistema):
 *   1. Firestore Timestamp (objeto com método .toDate)
 *   2. Plain object serializado {seconds, nanoseconds} (Timestamp no JSON)
 *   3. Date nativo do JavaScript
 *   4. String ISO ("2026-04-01" ou "2026-04-01T19:10:00.000Z")
 *   5. String BR ("01/04/2026")
 *
 * Retorna SEMPRE "YYYY-MM-DD" ou undefined.
 *
 * Crítico para a IA: Firestore Timestamp serializa como
 * {"seconds": N, "nanoseconds": M} via JSON.stringify. O LLM, ao receber
 * isso onde esperava data legível, alucina uma data plausível (caso AI_57
 * — 26/05/2026 inventada a partir de Timestamp serializado da consulta
 * de 01/04/2026 do Marcos).
 */
// B242 (AI_111): exportada para reuso pelo carregador da aba Pesquisa Clínica
// (normaliza datas heterogêneas do Firestore antes de comparar recência).
export function normalizarDataParaISO(raw: any): string {
  if (raw === null || raw === undefined || raw === "") return "";

  // 1. Firestore Timestamp (com método toDate)
  if (typeof raw === "object" && typeof raw.toDate === "function") {
    try {
      return raw.toDate().toISOString().substring(0, 10);
    } catch {
      return "";
    }
  }

  // 2. Plain object {seconds, nanoseconds} (Timestamp serializado)
  if (typeof raw === "object" && typeof raw.seconds === "number") {
    return new Date(raw.seconds * 1000).toISOString().substring(0, 10);
  }

  // 3. Date nativo
  if (raw instanceof Date) {
    return raw.toISOString().substring(0, 10);
  }

  // 4 e 5. String (ISO ou BR)
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return "";
    const matchISO = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (matchISO) return matchISO[0];
    const matchBR = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (matchBR) {
      const [, dia, mes, ano] = matchBR;
      return `${ano}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}`;
    }
    return trimmed;
  }

  return "";
}

// ─────────────────────────────────────────────────────────────────────────────
// B315 (AI_142) — VALORES LAB por DENYLIST (não mais whitelist fixa)
// ─────────────────────────────────────────────────────────────────────────────
// Antes: uma lista fixa de ~45 campos era copiada para `valores`; QUALQUER campo
// de exame fora da lista ficava INVISÍVEL à IA. A auditoria B315 achou ~15 campos
// clinicamente pesados descartados em silêncio (potássio, magnésio, cálcio total,
// TTPA, fibrinogênio, diferencial do hemograma, PCR, procalcitonina, TSH/T4,
// temperatura, diurese, e as DOSES de imunossupressor — só os NÍVEIS chegavam).
// Agora: TODO campo do exame passa, exceto os administrativos abaixo. Campo novo
// criado no futuro chega sozinho à IA (fim da classe de bug "esqueci de adicionar
// à whitelist"). Decisão de Leonardo (AI_142).
//
// PRIVACIDADE: o doc de exame não carrega PII do paciente (é indexado por
// pacienteId, que é bloqueado). Só passam PRIMITIVOS (number/string/boolean) —
// objetos/arrays (histórico/auditoria aninhados) nunca vazam. A rede B209
// (validarAnonimizacao) continua conferindo pós-hoc.
const CHAVES_META_EXAME_LAB = new Set<string>([
  "id",
  "data",
  "dataExame",
  "editavel",
  "po",
  "pacienteId",
  "registradoPor",
  "registradoPorNome",
  "editadoPor",
  "editadoPorNome",
  "dataEdicao",
  "criadoEm",
  "atualizadoEm",
  "analisePorIA",
  "historicoEdicoes",
  // Campos calculados no nível anonimizado (não pertencem a `valores`) — defesa
  // em profundidade caso um doc do Firestore traga essas chaves cruas.
  "horario",
  "coletaNumero",
]);

// Rótulos clínicos amigáveis. Mantém EXATAMENTE os rótulos históricos dos campos
// que a análise de tendências consome (AST/ALT/Plaquetas/Sodio/Tacrolimus/…) e
// desambigua DOSE × NÍVEL de imunossupressor. Campo sem rótulo aqui passa com a
// própria chave — é o que garante o futuro-à-prova (campo novo aparece na IA).
const ROTULO_VALOR_LAB: Record<string, string> = {
  tgo: "AST",
  tgp: "ALT",
  ggt: "GGT",
  fosfataseAlcalina: "FA",
  bilirrubinaTotal: "BT",
  bilirrubinaDireta: "BD",
  bilirrubinaIndireta: "BI",
  albumina: "Albumina",
  tap: "TAP",
  inr: "INR",
  ttpa: "TTPA",
  fibrinogenio: "Fibrinogenio",
  fatorV: "FatorV",
  creatinina: "Creatinina",
  ureia: "Ureia",
  sodio: "Sodio",
  potassio: "Potassio",
  magnesio: "Magnesio",
  calcio: "Calcio total",
  calcioIonico: "CalcioIonico",
  hemoglobina: "Hemoglobina",
  hematocrito: "Hematocrito",
  leucocitos: "Leucocitos",
  neutrofilos: "Neutrofilos",
  eosinofilos: "Eosinofilos",
  linfocitos: "Linfocitos",
  plaquetas: "Plaquetas",
  colesterolTotal: "ColesterolTotal",
  ldl: "LDL",
  hdl: "HDL",
  vldl: "VLDL",
  triglicerides: "Triglicerideos",
  glicemia: "Glicemia",
  hemoglobinaGlicada: "HbA1c",
  frutosamina: "Frutosamina",
  pcr: "PCR",
  procalcitonina: "Procalcitonina",
  lactato: "Lactato",
  amilase: "Amilase",
  lipase: "Lipase",
  acidoUrico: "AcidoUrico",
  fosforo: "Fosforo",
  cloro: "Cloro",
  bastonetes: "Bastonetes",
  ldh: "LDH",
  proteinasTotais: "ProteinasTotais",
  globulina: "Globulina",
  cpk: "CPK",
  ckMb: "CKMB",
  troponina: "Troponina",
  tsh: "TSH",
  t4Livre: "T4 livre",
  t3Total: "T3Total",
  gasometriaTipo: "GasometriaTipo",
  gasometriaPh: "GasometriaPh",
  gasometriaPco2: "GasometriaPco2",
  gasometriaPo2: "GasometriaPo2",
  gasometriaHco3: "GasometriaHco3",
  gasometriaBe: "GasometriaBe",
  gasometriaSaturacao: "GasometriaSaturacao",
  meld: "MELD",
  childPugh: "ChildPugh",
  ascite: "Ascite",
  encefalopatia: "Encefalopatia",
  // Imunossupressores — NÍVEL (mantém rótulo histórico p/ tendências) × DOSE (novo)
  nivelTacrolimus: "Tacrolimus",
  tacrolimus: "Tacrolimo (dose)",
  nivelCiclosporina: "Ciclosporina",
  ciclosporina: "Ciclosporina (dose)",
  nivelEverolimus: "Everolimus",
  everolimus: "Everolimo (dose)",
  nivelSirolimus: "Sirolimus",
  sirolimus: "Sirolimo (dose)",
  solumedrol: "Metilprednisolona (dose)",
  prednisona: "Prednisona (dose)",
  mmf: "Micofenolato mofetil (dose)",
  mms: "Micofenolato sodico (dose)",
  azatioprina: "Azatioprina (dose)",
  thymoglobulina: "Timoglobulina (dose)",
  // Vitais / status do exame pós-Tx (folhão)
  temperatura: "Temperatura (C)",
  peso: "Peso no exame (kg)",
  diurese: "Diurese (mL/24h)",
  dialise: "Dialise",
  transfusao: "Transfusao",
};

/**
 * Monta o objeto `valores` de um exame laboratorial por DENYLIST (B315/AI_142).
 * Todo campo do exame passa (com rótulo clínico quando houver), exceto as chaves
 * administrativas. Preserva as normalizações de contagem do B244.
 */
export function montarValoresLab(
  exame: Record<string, any>
): Record<string, number | string> {
  const valores: Record<string, number | string> = {};
  for (const chave of Object.keys(exame || {})) {
    if (CHAVES_META_EXAME_LAB.has(chave)) continue;
    let v = exame[chave];
    if (v === undefined || v === null || v === "") continue;
    // Só primitivos — objetos/arrays (auditoria/histórico aninhado) nunca passam.
    if (typeof v === "object") continue;
    // Normalização de contagens (plaquetas/leucócitos SI→/µL) — mantida do B244.
    if (chave === "leucocitos") v = normalizarContagemMilhar(v, 100);
    else if (chave === "plaquetas") v = normalizarContagemMilhar(v, 1000);
    if (typeof v === "boolean") v = v ? "Sim" : "Não";
    const rotulo = ROTULO_VALOR_LAB[chave] || chave;
    valores[rotulo] = v as number | string;
  }
  return valores;
}

/**
 * 🔐 FUNÇÃO PRINCIPAL: Anonimizar dados do paciente
 *
 * Remove informações identificáveis antes de enviar para API:
 * ❌ Nome
 * ❌ CPF
 * ❌ RG
 * ❌ Endereço completo
 * ❌ Telefones
 * ❌ Email
 * ❌ Nome da mãe
 *
 * ✅ Mantém:
 * - Idade (derivada da data de nascimento)
 * - Sexo
 * - Dados clínicos completos
 * - Timeline de eventos
 * - Achados estruturados de imagem
 */
export async function anonimizarDadosPaciente(
  paciente: Paciente,
  consultas: any[],
  examesLab: any[],
  marcadores: any[],
  sorologias: any[],
  imagens: any[],
  prescricoes?: any[],
  avaliacaoClinica?: any,
  dadosTransplante?: any[],
  inscricaoTx?: any,
  // B351 passo 5b (AI_153) — conjunto mínimo do RECEPTOR, já FECHADO e sem
  // identificadores pelo `montarConjuntoMinimoReceptor`. Chega pronto: este
  // módulo não decide o que entra, só transporta.
  receptorMinimo?: ConjuntoMinimoReceptor | null
): Promise<DadosAnonimizados> {
  // 🔑 Gerar hash único (mas consistente) para o paciente
  const pacienteHash = gerarHashPaciente(paciente.id);

  // 🎂 Calcular idade sem expor data de nascimento
  const idade = calcularIdade(paciente.dataNascimento);

  // 🏥 Dados do transplante (se aplicável)
  // Fallback: inscricaoTx (legado) → campos planos (novo)
  const dataTxResolve =
    paciente.inscricaoTx?.dataTransplante || paciente.dataTransplante;
  const transplante = dataTxResolve
    ? {
        dataTransplante: dataTxResolve,
        tempoTransplante: calcularTempoTransplante(dataTxResolve),
        diasPosTx: calcularDiasPosTx(dataTxResolve),
        tipoTransplante: paciente.tipoAtendimento || "figado",
        indicacao: paciente.inscricaoTx?.tipoSE || paciente.tipoSE || undefined,
        diagnosticoBase:
          paciente.inscricaoTx?.diagnosticoBase ||
          paciente.avaliacaoClinica?.causaCirrose ||
          undefined,
      }
    : undefined;

  // 🩺 Diagnóstico de base (importante para pré-TX)
  // Fallback: inscricaoTx (legado) → avaliacaoClinica.causaCirrose (novo)
  const diagnosticoBase =
    paciente.inscricaoTx?.diagnosticoBase ||
    paciente.avaliacaoClinica?.causaCirrose ||
    undefined;

  /// 📊 Scores pré-transplante
  // Fallback: inscricaoTx (legado) → scores (novo) → exames lab mais recente
  const meldResolve = paciente.inscricaoTx?.meld || paciente.scores?.meld;
  const meldNaResolve = paciente.inscricaoTx?.meldNa || paciente.scores?.meldNa;
  const childPughResolve = paciente.inscricaoTx?.childPugh;
  const childPughLabel =
    paciente.inscricaoTx?.childPugh || paciente.scores?.childPugh;

  const scores =
    meldResolve || meldNaResolve || childPughResolve
      ? {
          meld: meldResolve,
          meldNa: meldNaResolve,
          meld3: paciente.inscricaoTx?.meld3,
          childPugh: childPughResolve,
          classeChild: childPughResolve
            ? ((childPughResolve <= 6
                ? "A"
                : childPughResolve <= 9
                ? "B"
                : "C") as "A" | "B" | "C")
            : (childPughLabel as "A" | "B" | "C" | undefined),
        }
      : undefined;

  const consultasAnonimizadas = consultas.map((consulta) => ({
    id: consulta.id,
    data: normalizarDataParaISO(consulta.data || consulta.dataConsulta),
    // B315 (AI_142) — tipo da consulta (retorno/intercorrência/pré-Tx/pós-Tx):
    // a IA saber se é uma intercorrência aguda muda o enquadramento.
    tipoConsulta: consulta.tipoConsulta || undefined,
    queixaPrincipal: consulta.queixaPrincipal,
    // B90: no campo único (retorno/pós-Tx/intercorrência) a evolução livre entra
    // como história → a IA e os laudos enxergam o conteúdo do retorno.
    hma: consulta.historiaDoencaAtual || consulta.evolucaoLivre,
    // Revisão de sistemas — contém medicações em uso, comorbidades ativas, sintomas por aparelho
    revisaoSistemas: consulta.revisaoSistemas || undefined,
    exameFisico: consulta.exameFisico,
    // Sinais vitais — IMC, sarcopenia, hemodinâmica
    peso: consulta.peso || undefined,
    altura: consulta.altura || undefined,
    fc: consulta.fc || undefined,
    pa: consulta.pa || undefined,
    hipoteses: consulta.hipotesesDiagnosticas || consulta.hipoteses,
    conduta: consulta.conduta,
    observacoes: consulta.observacoes || undefined,
  }));
  // 🔬 Anonimizar exames laboratoriais
  // Ordenar por data + criadoEm ASCENDENTE (mais antigo primeiro → mais recente por último)
  // Isso garante que a IA leia a tendência cronológica corretamente
  const examesLabOrdenados = [...examesLab].sort((a: any, b: any) => {
    // Primeiro: ordenar por data do exame
    const dataA = a.data || a.dataExame || "";
    const dataB = b.data || b.dataExame || "";
    if (dataA !== dataB) return dataA.localeCompare(dataB);
    // Se mesma data: ordenar por criadoEm (timestamp de criação no Firebase)
    const tsA = a.criadoEm?.seconds || a.criadoEm?.getTime?.() / 1000 || 0;
    const tsB = b.criadoEm?.seconds || b.criadoEm?.getTime?.() / 1000 || 0;
    return tsA - tsB;
  });

  // Marcar coletas do mesmo dia com indicador sequencial
  const contagemPorData: Record<string, number> = {};
  const examesLabAnonimizados = examesLabOrdenados.map((exame: any) => {
    const dataExame = exame.data || exame.dataExame || "";
    contagemPorData[dataExame] = (contagemPorData[dataExame] || 0) + 1;
    const numeroColeta = contagemPorData[dataExame];

    // Extrair horário aproximado do criadoEm (se disponível)
    let horarioColeta: string | undefined;
    if (exame.criadoEm?.seconds) {
      const d = new Date(exame.criadoEm.seconds * 1000);
      horarioColeta = d.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "America/Porto_Velho",
      });
    }

    return {
      id: exame.id,
      data: dataExame,
      coletaNumero: numeroColeta,
      horario: horarioColeta,
      // B315 (AI_142) — DENYLIST: todo campo do exame passa (com rótulo clínico
      // quando houver), exceto chaves meta/PII. Ver montarValoresLab acima.
      valores: montarValoresLab(exame),
    };
  });

  // 🎯 Anonimizar marcadores tumorais E autoanticorpos
  // Estrutura real do Firebase: { afp: "2,1", ca199: "15", fan: "1/640", dataColeta: "2025-11-04" }
  const marcadoresAnonimizados: MarcadorAnonimizado[] = marcadores.map(
    (marcador) => ({
      id: marcador.id,
      dataColeta: marcador.dataColeta || marcador.data,
      // Marcadores tumorais
      afp: marcador.afp,
      ca199: marcador.ca199 || marcador["ca19-9"],
      cea: marcador.cea,
      ca125: marcador.ca125,
      psa: marcador.psa,
      // Metabolismo do ferro (B241 fase 2 — AI_109)
      ferro: marcador.ferro,
      ferritina: marcador.ferritina,
      saturacaoTransferrina: marcador.saturacaoTransferrina,
      // Metabólicos / Etiológicos (B195) — Wilson, deficiência de A1AT
      cobreSerico: marcador.cobreSerico,
      cobreUrinario: marcador.cobreUrinario,
      ceruloplasmina: marcador.ceruloplasmina,
      alfa1Antitripsina: marcador.alfa1Antitripsina,
      // Função renal / urina 24h (B279 — AI_120)
      clearanceCreatinina: marcador.clearanceCreatinina,
      clearanceCreatininaCorrigido: marcador.clearanceCreatininaCorrigido,
      proteinuria24h: marcador.proteinuria24h,
      // Sódio urinário / natriúria (AI_129)
      sodioUrinarioAmostra: marcador.sodioUrinarioAmostra,
      sodioUrinario24h: marcador.sodioUrinario24h,
      // Imunoglobulinas / Autoimune-IgG4 (B195) — HAI, doença IgG4
      iggSerico: marcador.iggSerico,
      igg4: marcador.igg4,
      // Eletroforese de proteínas — frações em g/dL (B195)
      efpAlbumina: marcador.efpAlbumina,
      efpAlfa1: marcador.efpAlfa1,
      efpAlfa2: marcador.efpAlfa2,
      efpBeta: marcador.efpBeta,
      efpGama: marcador.efpGama,
      // Autoanticorpos — essenciais para HAI, CBP, CEP, overlap
      fan: marcador.fan, // FAN/ANA (ex: "1/640")
      antiMusculoLiso: marcador.antiMusculoLiso, // ASMA (ex: "1/160")
      antiLKM: marcador.antiLKM, // anti-LKM1 (HAI tipo 2)
      antiMitocondria: marcador.antiMitocondria, // AMA (CBP — patognomônico)
      anca: marcador.anca, // pANCA (CEP)
      antiDNA: marcador.antiDNA, // anti-dsDNA (lúpus/overlap)
      antiTransglutaminase: marcador.antiTransglutaminase, // Doença celíaca
    })
  ).map((m: any) =>
    padronizarRegistroMarcadores(m, { manterOriginal: true })
  );

  // 🦠 Anonimizar sorologias
  // B241 fase 2 (AI_109) — CONSERTO: a versão antiga mapeava `tipo`/`resultado`
  // (campos que NÃO existem em SorologiaViral) → as sorologias praticamente não
  // chegavam à IA. Agora passamos os CAMPOS REAIS (resultados qualitativos/cargas
  // virais/genótipo), sem dados identificáveis (id/autoria/pacienteId ficam fora).
  const sorologiasAnonimizadas = sorologias.map((s: any) => ({
    data: s.dataColeta || s.data,
    dataColeta: s.dataColeta || s.data,
    // Hepatite B
    hbsag: s.hbsag,
    antiHBs: s.antiHBs,
    antiHBcIgG: s.antiHBcIgG,
    antiHBcIgM: s.antiHBcIgM,
    hbeag: s.hbeag,
    antiHBe: s.antiHBe,
    genotipagemVHB: s.genotipagemVHB,
    pcrVHB: s.pcrVHB,
    // Hepatite C
    antiHCV: s.antiHCV,
    hcvRNA: s.hcvRNA,
    // Hepatite A
    antiHAVIgG: s.antiHAVIgG,
    antiHAVIgM: s.antiHAVIgM,
    // Hepatite D/E
    antiHDV: s.antiHDV,
    pcrVHD: s.pcrVHD,
    antiHEV: s.antiHEV,
    // CMV / EBV / Rubéola
    cmvIgG: s.cmvIgG,
    cmvIgM: s.cmvIgM,
    cmvPCR: s.cmvPCR,
    ebvIgG: s.ebvIgG,
    ebvIgM: s.ebvIgM,
    ebvPCR: s.ebvPCR,
    rubeolaIgG: s.rubeolaIgG,
    rubeolaIgM: s.rubeolaIgM,
    // Outros
    antiHIV: s.antiHIV,
    vdrl: s.vdrl,
    ftaAbs: s.ftaAbs,
    toxoIgG: s.toxoIgG,
    toxoIgM: s.toxoIgM,
    chagas: s.chagas,
    chagasIgG: s.chagasIgG,
    chagasIgM: s.chagasIgM,
    htlv: s.htlv,
    // Rastreio pré-Tx — parasitas / TB (AI_118)
    estrongiloidesIgG: s.estrongiloidesIgG,
    igraTB: s.igraTB,
    ppd: s.ppd,
  })).map((s: any) =>
    padronizarRegistroSorologia(s, { manterOriginal: true })
  );

  // 📷 Anonimizar exames de imagem COM ACHADOS ESTRUTURADOS
  const imagensAnonimizadas = imagens.map((imagem) => ({
    id: imagem.id,
    data: imagem.dataExame || imagem.data,
    tipo: imagem.tipoExame || imagem.tipo,
    descricao: imagem.descricao,
    // B315-Fase4 (AI_142) — laudo + indicação: p/ exames NÃO analisados pela IA,
    // é o único conteúdo clínico que a análise geral pode ler (antes ficavam
    // invisíveis; só o chat mostrava a existência do exame).
    laudo: imagem.laudo || undefined,
    indicacaoClinica: (imagem as any).indicacaoClinica || undefined,
    achados: imagem.achados || undefined,
    resumoAchados: imagem.resumoAchados || undefined,
    analiseCompleta: (imagem as any).analiseCompleta || undefined,
    analisadoPorIA: !!(imagem.achados || (imagem as any).analiseCompleta),
  }));

  // ✅ NOVO: Extrair achados estruturados de imagem para análise
  const achadosImagem: AchadosImagemEstruturados[] = imagens
    .filter((img) => img.achados || (img as any).analiseCompleta) // Exames analisados (estruturados OU texto)
    .map((img) => ({
      exameId: img.id,
      dataExame: img.dataExame || img.data,
      tipoExame: img.tipoExame || img.tipo,
      analiseCompleta: (img as any).analiseCompleta || undefined,
      lesoesHepaticas: img.achados?.lesoesHepaticas,
      statusMilao: img.achados?.statusMilao,
      trombosePortal: img.achados?.trombosePortal,
      tipoTrombose: img.achados?.tipoTrombose,
      shunts: img.achados?.shunts,
      ascite: img.achados?.ascite,
      esplenomegalia: img.achados?.esplenomegalia,
      outrosAchados: img.achados?.outrosAchados,
      conclusao: img.achados?.conclusao,
      resumoAchados: img.resumoAchados,
      // ✅ Bloco 1 AI_34 — propaga transcrição literal do laudista
      conclusaoLaudistaLiteral: img.achados?.conclusaoLaudistaLiteral,
      tipoLaudista: img.achados?.tipoLaudista,
      conclusaoLiteralGeradaEm: img.achados?.conclusaoLiteralGeradaEm,
      conclusaoLiteralStatus: img.achados?.conclusaoLiteralStatus,
      // ✅ AI_40 Patch 2 — adequação amostral (biópsias): confiabilidade do laudo
      adequacaoAmostral: img.achados?.adequacaoAmostral,
      // ✅ B315 (AI_142) — endoscopia (B301) e elastografia (B138) estruturadas +
      // contexto do pedido. Extraídos/salvos, agora propagados à análise IA.
      varizesEsofagicas: img.achados?.varizesEsofagicas,
      varizesGastricas: img.achados?.varizesGastricas,
      gastropatiaHipertensiva: img.achados?.gastropatiaHipertensiva,
      sinaisRisco: img.achados?.sinaisRisco,
      gave: img.achados?.gave,
      classificacaoRisco: img.achados?.classificacaoRisco,
      rigidezKpa: img.achados?.rigidezKpa,
      fibroseMetavir: img.achados?.fibroseMetavir,
      capEsteatose: img.achados?.capEsteatose,
      indicacaoClinica: (img as any).indicacaoClinica || undefined,
    }))
    .sort((a, b) => {
      // Ordenar por data (mais recente primeiro)
      const dataA = new Date(a.dataExame).getTime();
      const dataB = new Date(b.dataExame).getTime();
      return dataB - dataA;
    });

  // 📈 Calcular tendências laboratoriais (pré-processamento, custo zero na API)
  const tendenciasLab = calcularTendenciasLaboratoriais(
    examesLabAnonimizados,
    transplante?.diasPosTx
  );
  // 📦 Montar objeto anonimizado
  const dadosAnonimizados: DadosAnonimizados = {
    pacienteHash,
    idade,
    sexo: paciente.sexo,
    tipoAtendimento: paciente.tipoAtendimento || "nao-especificado",
    comorbidades: paciente.comorbidades || undefined,
    peso: paciente.peso || undefined,
    altura: paciente.altura || undefined,
    alergias: paciente.alergias || undefined,
    medicacoesEmUso: paciente.medicacoesEmUso || undefined,
    transplante,
    diagnosticoBase,
    scores,
    consultas: consultasAnonimizadas,
    examesLab: examesLabAnonimizados,
    marcadores: marcadoresAnonimizados,
    sorologias: sorologiasAnonimizadas,
    imagens: imagensAnonimizadas,
    achadosImagem,
    tendenciasLab:
      Object.keys(tendenciasLab).length > 0 ? tendenciasLab : undefined,
    // B358-FU5 (AI_156): MELD-Na de alocação vigente em SP, no próprio dado.
    meldNaAlocacaoSP:
      meldNaDoExameMaisRecente(
        examesLabAnonimizados,
        paciente.avaliacaoClinica?.dialise === true
      ) || undefined,
    // B351 passo 5b (AI_153) — DOADOR VIVO. Só nesta modalidade, e por whitelist
    // (receptorId/receptorNome ficam de fora por construção — ver
    // avaliacaoDoadorParaIA). Os derivados (GRWR/remanescente/IMC) vêm
    // calculados pelo sistema: a IA confere, não recalcula.
    avaliacaoDoador: ehDoadorFigado(paciente.tipoAtendimento)
      ? montarAvaliacaoDoadorParaIA((paciente as any).avaliacaoDoador) || undefined
      : undefined,
    // Conjunto mínimo do RECEPTOR (cruzamento de prontuários) — chega pronto e
    // fechado do coletor; nunca é montado aqui.
    receptorMinimo: receptorMinimo || undefined,
    avaliacaoClinica: avaliacaoClinica || undefined,
    dadosTransplante:
      dadosTransplante?.map((tx: any) => ({
        ordem: tx.ordem,
        dataTransplante: tx.dataTransplante,
        motivoRetx: tx.motivoRetx,
        equipeCirurgica: tx.equipeCirurgica,
        posOperatorio: tx.posOperatorio,
      })) || undefined,
    inscricaoTx: {
      // B376 (AI_156): o status fossilizava após o transplante e a IA gastava
      // uma recomendação pedindo "correção administrativa" (caso real, B363).
      // Re-listagem para RETRANSPLANTE é preservada — ver statusInscricaoIA.ts.
      statusInscricao: statusInscricaoParaIA({
        statusInscricao: inscricaoTx?.statusInscricao,
        dataInscricao:
          inscricaoTx?.dataInscricao ||
          paciente.dataInscricao ||
          paciente.dataInclusaoLista,
        dataTransplante: dataTxResolve,
      }),
      dataInscricao:
        inscricaoTx?.dataInscricao ||
        paciente.dataInscricao ||
        paciente.dataInclusaoLista ||
        undefined,
      // B360 (AI_156): o NÚMERO do RGCT não sai para a IA — vai um marcador e
      // o número real volta só no navegador (reidentificarObjeto em callAnalyze).
      numeroRGCT: (() => {
        const rgct =
          inscricaoTx?.numeroRGCT || paciente.numeroRGCT || paciente.rgct;
        return rgct ? registrarRGCT(pacienteHash, String(rgct)) : undefined;
      })(),
      situacaoEspecial:
        inscricaoTx?.situacaoEspecial === true ||
        paciente.situacaoEspecial === true,
      tipoSE: inscricaoTx?.tipoSE || paciente.tipoSE || undefined,
      diagnosticoBase:
        inscricaoTx?.diagnosticoBase ||
        paciente.avaliacaoClinica?.causaCirrose ||
        undefined,
    },
    prescricoes:
      prescricoes?.map((p: any) => ({
        tipo: p.tipo,
        titulo: p.titulo,
        data: p.criadoEm?.toDate?.()?.toISOString?.() || p.criadoEm || "",
        conteudo: p.conteudo,
      })) || undefined,
  };

  // B315-Fase4-FU (AI_142) — redação estrutural de PII em TODO texto livre (defesa
  // em profundidade). Mesmo que um laudo/evolução tenha CPF/telefone/e-mail
  // digitado à mão, não chega à IA. Ver redigirPiiEmObjeto/redigirPiiEstrutural.
  // B360 (AI_156) — e o NOME da própria paciente que tenha sido digitado em
  // texto livre (cartas, receitas, evoluções) vira "[paciente]".
  const rgctReal =
    inscricaoTx?.numeroRGCT || paciente.numeroRGCT || (paciente as any).rgct;
  let saida = redigirNomeEmObjeto(
    redigirPiiEmObjeto(dadosAnonimizados),
    padroesNomePaciente((paciente as any).nome)
  );
  // B372 (AI_156): o NOME DA MÃE também era digitado em texto livre e chegava à
  // IA (acusado pelo detector B209 em 30/10). Identificador forte no Brasil —
  // vira "[mãe]". Só o nome COMPLETO, para não apagar termo clínico.
  saida = redigirNomeEmObjeto(
    saida,
    padroesNomeMae((paciente as any).nomeMae),
    "[mãe]"
  );
  // B360-FU (AI_156): o RGCT também é DIGITADO em texto livre (cartas à Central,
  // evoluções) — o campo estruturado já virava marcador, mas o texto vazava.
  if (rgctReal) {
    saida = substituirRGCTEmTexto(
      saida,
      String(rgctReal),
      marcadorRGCT(pacienteHash)
    );
  }
  return saida;
}

/**
 * 🔒 B315-Fase4-FU (AI_142) — Redação ESTRUTURAL de PII em texto livre.
 *
 * Apaga CPF, CNPJ, telefone e e-mail de strings que vão à IA. São padrões que
 * NUNCA aparecem em texto clínico legítimo (medidas/valores não têm 10-11 dígitos
 * corridos nem @) → zero falso positivo sobre conteúdo médico. NÃO toca NOMES
 * (risco de apagar termo clínico); o nome do próprio paciente já é sinalizado pela
 * rede B209 (validarAnonimizacao). Defesa em profundidade — a barreira primária
 * continua sendo a remoção por construção dos campos identificadores do paciente.
 */
export function redigirPiiEstrutural(texto: string): string {
  if (!texto || typeof texto !== "string") return texto;
  return texto
    // e-mail (padrão inconfundível)
    .replace(
      /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,
      "[e-mail removido]"
    )
    // CNPJ formatado (antes do CPF, formato distinto)
    .replace(/\b\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}\b/g, "[CNPJ removido]")
    // CPF formatado
    .replace(/\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/g, "[CPF removido]")
    // telefone com DDD e separador (ex.: (11) 98765-4321, 11 98765-4321)
    .replace(/\(?\d{2}\)?[\s.-]?9?\d{4}[\s.-]\d{4}\b/g, "[telefone removido]")
    // sequência crua de 10-11 dígitos = CPF/telefone sem formatação (texto
    // clínico não tem números tão longos corridos; datas têm ≤8)
    .replace(/\b\d{10,11}\b/g, "[número removido]");
}

/** Aplica redigirPiiEstrutural recursivamente a todas as strings de um objeto. */
export function redigirPiiEmObjeto<T>(valor: T): T {
  if (typeof valor === "string") return redigirPiiEstrutural(valor) as any;
  if (Array.isArray(valor)) {
    return valor.map((v) => redigirPiiEmObjeto(v)) as any;
  }
  if (valor && typeof valor === "object") {
    const out: Record<string, any> = {};
    for (const k of Object.keys(valor as any)) {
      out[k] = redigirPiiEmObjeto((valor as any)[k]);
    }
    return out as any;
  }
  return valor;
}

/**
 * 📈 Calcular tendências laboratoriais dos últimos exames
 * Pré-processamento: zero custo de API, melhora significativa na interpretação
 */
// B244 (AI_110) — RECÊNCIA das tendências. Um analito cujo ÚLTIMO valor seja
// mais antigo que isto, em relação à coleta mais recente do paciente, não é
// uma "tendência atual" (ex.: Lactato só medido no pós-op imediato não deve
// virar alerta meses depois). Ajustável por Leonardo (decisão clínica).
const LIMIAR_RECENCIA_DIAS = 90;

// B244 (AI_110) — Normaliza CONTAGENS (plaquetas, leucócitos) para a convenção
// /µL que o sistema usa. Um valor abaixo de `limiar` é não-fisiológico em /µL →
// é abreviação de milhares (ex.: "233" ou "233.000" — que o parse lê como 233 —
// querem dizer 233.000) ou unidade SI (10⁹/L). Em ambos os casos, ×1000.
// Preserva o valor original quando vazio/não-numérico. limiar: 1000 p/ plaquetas
// (mín. fisiológico ~10.000), 100 p/ leucócitos (mín. fisiológico ~500).
// B242 (AI_111): exportada para reuso pelo carregador da aba Pesquisa Clínica
// (mesma normalização de plaquetas/leucócitos SI→/µL aplicada à IA).
export function normalizarContagemMilhar(
  raw: any,
  limiar: number
): any {
  if (raw === undefined || raw === null || raw === "") return raw;
  const v = parseFloat(String(raw).replace(",", "."));
  if (isNaN(v) || v <= 0) return raw; // não-numérico → preserva
  return v < limiar ? v * 1000 : v;
}

// Diferença em dias entre duas datas ISO "YYYY-MM-DD" (b - a); null se inválidas.
function diffDiasISO(a: string, b: string): number | null {
  if (!a || !b) return null;
  const da = Date.parse(a + "T00:00:00");
  const db = Date.parse(b + "T00:00:00");
  if (isNaN(da) || isNaN(db)) return null;
  return Math.round((db - da) / 86400000);
}

function calcularTendenciasLaboratoriais(
  examesOrdenados: any[],
  diasPosTx?: number
): Record<
  string,
  {
    valores: { data: string; valor: number }[];
    direcao: string;
    variacao: number | null;
    alerta: string | null;
  }
> {
  // Analitos-chave para monitorar tendências.
  // - limiteAlerta: limiar que dispara um ALERTA de tendência (valor de atenção).
  // - refNormal (B244, AI_110): FAIXA NORMAL de referência. Quando os DOIS valores
  //   comparados estão dentro dela, a variação percentual NÃO tem significado
  //   clínico (ex.: AST 16,5→28,5 = +72,7% sem importância) e é suprimida, para
  //   não gerar ruído que a IA precise "desmentir". Sem refNormal (ex.: Tacrolimus,
  //   alvo dependente do regime), a variação é sempre exibida.
  const analitosChave: {
    campo: string;
    nome: string;
    limiteAlerta?: { alto?: number; baixo?: number };
    refNormal?: { alto?: number; baixo?: number };
  }[] = [
    { campo: "AST", nome: "AST", limiteAlerta: { alto: 200 }, refNormal: { alto: 40 } },
    { campo: "ALT", nome: "ALT", limiteAlerta: { alto: 200 }, refNormal: { alto: 40 } },
    { campo: "GGT", nome: "GGT", limiteAlerta: { alto: 300 }, refNormal: { alto: 60 } },
    { campo: "FA", nome: "FA", limiteAlerta: { alto: 300 }, refNormal: { alto: 120 } },
    { campo: "BT", nome: "Bilirrubina Total", limiteAlerta: { alto: 3 }, refNormal: { alto: 1.2 } },
    { campo: "Creatinina", nome: "Creatinina", limiteAlerta: { alto: 1.5 }, refNormal: { alto: 1.3 } },
    { campo: "INR", nome: "INR", limiteAlerta: { alto: 1.5 }, refNormal: { alto: 1.3 } },
    { campo: "Albumina", nome: "Albumina", limiteAlerta: { baixo: 3.5 }, refNormal: { baixo: 3.5 } },
    { campo: "Hemoglobina", nome: "Hemoglobina", limiteAlerta: { baixo: 10 }, refNormal: { baixo: 12 } },
    { campo: "Plaquetas", nome: "Plaquetas", limiteAlerta: { baixo: 100000 }, refNormal: { baixo: 150000 } },
    { campo: "Sodio", nome: "Sódio", limiteAlerta: { baixo: 135 }, refNormal: { baixo: 135, alto: 145 } },
    { campo: "Tacrolimus", nome: "Tacrolimus" },
    { campo: "Lactato", nome: "Lactato", limiteAlerta: { alto: 2 }, refNormal: { alto: 2 } },
    // B241 fase 2 (AI_109) — novos analitos monitorados
    { campo: "Amilase", nome: "Amilase", limiteAlerta: { alto: 330 }, refNormal: { alto: 110 } },
    { campo: "Lipase", nome: "Lipase", limiteAlerta: { alto: 180 }, refNormal: { alto: 60 } },
    { campo: "AcidoUrico", nome: "Ácido úrico", limiteAlerta: { alto: 7 }, refNormal: { alto: 7 } },
    { campo: "CalcioIonico", nome: "Cálcio iônico", limiteAlerta: { alto: 1.35, baixo: 1.1 }, refNormal: { alto: 1.35, baixo: 1.1 } },
    { campo: "T3Total", nome: "T3 total" },
    // B315-Fase3 (AI_142) — trajetória dos labs de alto valor recém-integrados.
    // Potássio: unidade estável (mEq/L), alerta em ambos os extremos (hipercalemia
    // com espironolactona/tacrolimo × hipocalemia por diurético). PCR/Procalcitonina:
    // SEM limiteAlerta/refNormal de propósito (unidade varia mg/L×mg/dL entre labs)
    // → mostra a CURVA (ex.: PCR 5→80→150) sem risco de alerta por erro de unidade.
    { campo: "Potassio", nome: "Potássio", limiteAlerta: { alto: 5.5, baixo: 3.0 }, refNormal: { alto: 5.0, baixo: 3.5 } },
    { campo: "PCR", nome: "PCR" },
    { campo: "Procalcitonina", nome: "Procalcitonina" },
    // B342 (AI_150) — dos 9 campos do folhão, só estes dois têm valor de
    // TRAJETÓRIA no pós-operatório; os demais (cloro, bastonetes, proteínas,
    // globulina, CPK, CK-MB, troponina) chegam à IA como valor, sem curva.
    // Fósforo: hipofosfatemia é esperada na regeneração hepática e na
    // realimentação — a curva importa. Unidade estável (mg/dL) → tem referência.
    { campo: "Fosforo", nome: "Fósforo", limiteAlerta: { baixo: 2.0, alto: 5.5 }, refNormal: { baixo: 2.5, alto: 4.5 } },
    // LDH: útil na isquemia-reperfusão do enxerto e na hemólise, mas o valor de
    // referência varia com o método → SEM limite (mostra a curva), como o PCR.
    { campo: "LDH", nome: "LDH" },
  ];

  const tendencias: Record<
    string,
    {
      valores: { data: string; valor: number }[];
      direcao: string;
      variacao: number | null;
      alerta: string | null;
    }
  > = {};

  // Coleta mais recente do paciente (examesOrdenados está em ordem ascendente).
  // Serve de âncora para o filtro de recência por analito.
  let dataMaisRecenteGeral = "";
  for (let i = examesOrdenados.length - 1; i >= 0; i--) {
    const d = examesOrdenados[i]?.data;
    if (d) {
      dataMaisRecenteGeral = d;
      break;
    }
  }

  for (const analito of analitosChave) {
    // Extrair valores não-nulos deste analito de todos os exames (já ordenados cronologicamente)
    const valoresExtraidos: { data: string; valor: number }[] = [];

    for (const exame of examesOrdenados) {
      const raw = exame.valores?.[analito.campo];
      if (raw !== undefined && raw !== null && raw !== "") {
        const valor = parseFloat(String(raw).replace(",", "."));
        if (!isNaN(valor) && valor > 0) {
          valoresExtraidos.push({
            data: exame.data || "",
            valor,
          });
        }
      }
    }

    // Precisamos de pelo menos 2 valores para calcular tendência
    if (valoresExtraidos.length < 2) continue;

    // Pegar os últimos 4 valores (suficiente para tendência sem sobrecarregar)
    const ultimos = valoresExtraidos.slice(-4);
    const penultimo = ultimos[ultimos.length - 2];
    const ultimo = ultimos[ultimos.length - 1];

    // B244 (AI_110) — RECÊNCIA: a tendência só é "atual" se o último valor veio
    // de uma coleta recente vs a coleta mais recente do paciente. Caso contrário,
    // um analito medido só no pós-op imediato (ex.: Lactato) viraria um "alerta"
    // meses depois. Desatualizado → não alarmamos (a data exibida deixa claro).
    const diasDesatualizado = diffDiasISO(ultimo.data, dataMaisRecenteGeral);
    const desatualizado =
      diasDesatualizado !== null && diasDesatualizado > LIMIAR_RECENCIA_DIAS;

    // Calcular variação percentual
    const variacao =
      penultimo.valor !== 0
        ? ((ultimo.valor - penultimo.valor) / penultimo.valor) * 100
        : null;

    // B244 (AI_110) — FAIXA NORMAL: se os DOIS valores comparados estão dentro da
    // faixa de referência, a variação percentual é ruído clínico (ex.: AST
    // 16,5→28,5 = +72,7% sem importância). Sem refNormal (ex.: Tacrolimus), não
    // suprime (variação sempre exibida).
    const ref = analito.refNormal;
    const dentroRef = (v: number) =>
      !!ref &&
      (ref.alto === undefined || v <= ref.alto) &&
      (ref.baixo === undefined || v >= ref.baixo);
    const ambosNormais = dentroRef(penultimo.valor) && dentroRef(ultimo.valor);

    // Suprimir a tendência (mantém os VALORES, mas zera seta/%/alerta enganosos)
    // quando: BT < 2,0 (legado, sem significado clínico), analito desatualizado,
    // ou os dois valores dentro da faixa normal.
    // NOTA (B244): NÃO se suprime "salto grande" de forma cega — uma queda real e
    // crítica (ex.: plaquetas 450.000→4.000) também é um salto. O erro de unidade
    // (233 vs 255000) é resolvido na ORIGEM por normalizarContagemMilhar, sem
    // mascarar mudanças clínicas verdadeiras.
    if (
      (analito.campo === "BT" && ultimo.valor < 2.0) ||
      desatualizado ||
      ambosNormais
    ) {
      tendencias[analito.campo] = {
        valores: ultimos,
        direcao: "→",
        variacao: null,
        alerta: null,
      };
      continue;
    }

    // Determinar direção com seta
    let direcao = "→"; // estável
    if (variacao !== null) {
      if (variacao > 20) direcao = "↑"; // subindo forte
      else if (variacao > 5) direcao = "↗"; // subindo leve
      else if (variacao < -20) direcao = "↓"; // caindo forte
      else if (variacao < -5) direcao = "↘"; // caindo leve
    }

    // Verificar se o último valor cruza um limiar de alerta
    let alerta: string | null = null;
    if (analito.limiteAlerta) {
      if (
        analito.limiteAlerta.alto &&
        ultimo.valor > analito.limiteAlerta.alto
      ) {
        if (direcao === "↑" || direcao === "↗") {
          alerta = "ELEVADO E SUBINDO";
        } else if (direcao === "→") {
          alerta = "ELEVADO PERSISTENTE";
        }
      }
      if (
        analito.limiteAlerta.baixo &&
        ultimo.valor < analito.limiteAlerta.baixo
      ) {
        if (direcao === "↓" || direcao === "↘") {
          alerta = "BAIXO E CAINDO";
        } else if (direcao === "→") {
          alerta = "BAIXO PERSISTENTE";
        }
      }
    }

    // Notas contextuais para pós-Tx precoce (não suprimem dados, contextualizam)
    let alertaFinal = alerta;

    if (diasPosTx !== undefined) {
      // GGT em D0-D60: elevação fisiológica (regeneração/glutationa)
      // Alarme apenas se acompanhada de icterícia ou suspeita vascular
      if (analito.campo === "GGT" && diasPosTx <= 60) {
        if (
          alertaFinal === "ELEVADO E SUBINDO" ||
          alertaFinal === "ELEVADO PERSISTENTE"
        ) {
          alertaFinal =
            "ELEVADO (pós-Tx D" +
            diasPosTx +
            " — elevação fisiológica esperada; alarme apenas se BT em ascensão ou alteração vascular ao Doppler)";
        }
      }

      // AST/ALT em D0-D7: IRI esperada se <2000; EAD se >2000
      if (
        (analito.campo === "AST" || analito.campo === "ALT") &&
        diasPosTx <= 7
      ) {
        if (ultimo.valor > 2000) {
          alertaFinal =
            "CRITÉRIO DE EAD — " +
            analito.campo +
            " >" +
            "2000 em D" +
            diasPosTx +
            " pós-Tx (Olthoff criteria)";
        } else if (alertaFinal) {
          alertaFinal =
            alertaFinal +
            " (IRI esperada em D" +
            diasPosTx +
            " pós-Tx; resolução habitual em 3-5 dias)";
        }
      }

      // Hemoglobina em D0-D14: queda pós-operatória esperada (hemodiluição + sangramento intraop)
      if (analito.campo === "Hemoglobina" && diasPosTx <= 14) {
        if (alertaFinal === "BAIXO E CAINDO") {
          alertaFinal =
            "BAIXO E CAINDO (queda pós-operatória esperada em D" +
            diasPosTx +
            "; investigar sangramento ativo apenas se Hb <7 ou queda >3g/dL/24h)";
        }
      }
    }

    tendencias[analito.campo] = {
      valores: ultimos,
      direcao,
      variacao: variacao !== null ? Math.round(variacao * 10) / 10 : null,
      alerta: alertaFinal,
    };
  }

  return tendencias;
}

/**
 * 🔑 Gerar hash único para o paciente
 * (Consistente para o mesmo ID, mas não reversível)
 */
// B242 (AI_111): exportada para reuso pelo carregador da aba Pesquisa Clínica —
// o MESMO pseudônimo da IA, garantindo consistência entre análise e pesquisa.
export function gerarHashPaciente(pacienteId: string): string {
  const salt = "HEPATO_TX_RO_2025";
  const texto = pacienteId + salt;
  let hash = 0;
  for (let i = 0; i < texto.length; i++) {
    const char = texto.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return `PAC_${Math.abs(hash).toString(36).toUpperCase()}`;
}

/**
 * 🎂 Calcular idade a partir da data de nascimento
 */
function calcularIdade(dataNascimento: string): number {
  const nascimento = new Date(dataNascimento);
  const hoje = new Date();
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const mesAtual = hoje.getMonth();
  const mesNascimento = nascimento.getMonth();
  if (
    mesAtual < mesNascimento ||
    (mesAtual === mesNascimento && hoje.getDate() < nascimento.getDate())
  ) {
    idade--;
  }
  return idade;
}

/**
 * ⏱️ Calcular tempo desde transplante
 */
function calcularTempoTransplante(dataTransplante: string): string {
  const [ano, mes, dia] = dataTransplante.split("-").map(Number);
  const dataTx = new Date(ano, mes - 1, dia);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  dataTx.setHours(0, 0, 0, 0);
  const diffMs = hoje.getTime() - dataTx.getTime();
  const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const anos = Math.floor(diffDias / 365);
  const mesesRestantes = Math.floor((diffDias % 365) / 30);
  const diasRestantes = Math.floor((diffDias % 365) % 30);
  return `${anos}a ${mesesRestantes}m ${diasRestantes}d`;
}

/**
 * ⏱️ Retorna número de dias desde o transplante (para lógica condicional)
 */
function calcularDiasPosTx(dataTransplante: string): number {
  const [ano, mes, dia] = dataTransplante.split("-").map(Number);
  const dataTx = new Date(ano, mes - 1, dia);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  dataTx.setHours(0, 0, 0, 0);
  const diffMs = hoje.getTime() - dataTx.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * ✅ Validar anonimização — defesa em profundidade (B209/B194).
 *
 * Confere que NENHUM valor identificável do paciente original vazou para a
 * saída anonimizada. Compara os VALORES reais (nome, CPF, RG, e-mail, etc.),
 * não substrings genéricas — isso elimina o falso-positivo da versão antiga
 * (que reprovava por nomes de campo: "alergias" ⊃ "rg").
 *
 * A barreira PRIMÁRIA é a remoção por construção em `anonimizarDadosPaciente`
 * (que monta um objeto novo só com campos seguros). Esta função é a SEGUNDA
 * checagem: detecta uma eventual regressão futura. Não bloqueia nada — quem
 * consome decide (hoje: log de alerta em `coletarDadosPaciente`, sem
 * interromper).
 *
 * Retorna a lista de campos cujo valor foi encontrado na saída (vazia = ok).
 * NÃO retorna o valor em si (LGPD): só o NOME do campo, seguro para log.
 */
// B309/M5 (AI_140) — à prova de EVOLUÇÃO DE SCHEMA: em vez de uma lista fixa,
// varre TODAS as chaves de `pacienteOriginal` cujo NOME casa este padrão de
// identificador (pega campos novos como `coren`/`cns`/`rgct`/`contatoEmergenciaNome`
// sem exigir manutenção). Casa por SUBSTRING no nome (case-insensitive), então
// `nomeMae`/`dataNascimento`/`contatoEmergenciaTelefone` entram naturalmente.
// NÃO casa dado clínico legítimo que a anonimização mantém de propósito
// (etiologia, sexo, labs) → sem falso positivo. Não usa `crm`/`cidade`/`estado`
// (podem aparecer clinicamente e não são PII direta do paciente).
const PADRAO_CAMPO_IDENTIFICADOR =
  /(nome|cpf|\brg\b|rgct|\bcns\b|coren|telefone|celular|\bfone\b|email|e-?mail|endereco|logradouro|bairro|\bcep\b|nascimento|\bmae\b|\bpai\b|matricula|prontuario|hospub|passaporte|contato|responsavel|filiacao|naturalidade)/i;

export function validarAnonimizacao(
  dados: DadosAnonimizados,
  pacienteOriginal?: Partial<Paciente>
): { ok: boolean; camposVazados: string[] } {
  if (!pacienteOriginal) {
    // Sem o paciente original não há o que comparar — considera ok (a barreira
    // primária é a remoção na origem; esta checagem é opcional).
    return { ok: true, camposVazados: [] };
  }
  const json = JSON.stringify(dados);
  const camposVazados: string[] = [];
  for (const campo of Object.keys(pacienteOriginal)) {
    if (!PADRAO_CAMPO_IDENTIFICADOR.test(campo)) continue;
    const bruto = (pacienteOriginal as Record<string, unknown>)[campo];
    // Aceita string; um Date/valor não-string vira string (pega dataNascimento
    // gravada como Date, achado do M5).
    const valor =
      typeof bruto === "string"
        ? bruto
        : bruto instanceof Date
        ? bruto.toISOString().slice(0, 10)
        : "";
    const v = valor.trim();
    // valores muito curtos (<3) causam colisão espúria; identificadores reais
    // (nome completo, CPF, e-mail…) são longos.
    if (v.length >= 3 && json.includes(v)) {
      camposVazados.push(campo);
    }
  }
  return { ok: camposVazados.length === 0, camposVazados };
}
